/**
 * 라우트별 SEO 메타데이터 갱신
 *
 * 이 프로젝트는 CSR SPA라서 모든 URL이 같은 index.html을 받는다.
 * index.html의 메타는 "홈 기준 기본값"이고, 라우트가 바뀔 때 이 파일이
 * title / description / canonical / og:* / robots 를 실제 페이지에 맞게 덮어쓴다.
 *
 * 왜 필요한가:
 *  - canonical이 전 페이지 동일하면 검색엔진이 하위 페이지를 홈의 중복으로 보고 색인에서 버린다.
 *  - 페이지별 title이 없으면 업종별 활용사례 9개가 전부 같은 제목으로 경쟁한다.
 *
 * 주의: 네이버 크롤러는 JS 렌더링이 약해 여기서 넣은 값을 못 볼 수 있다.
 *       구글은 렌더링 후 값을 사용한다. (SSR/프리렌더 도입 시 이 파일은 그대로 두고
 *       빌드 타임에 동일한 SEO_MAP을 재사용하면 된다.)
 */

export const SITE_URL = 'https://barosquare.com'
/** 공유 카드 이미지 (1200x630). index.html의 og:image와 반드시 같은 값으로 유지할 것. */
const OG_IMAGE = `${SITE_URL}/img/og-image.jpg`

export interface PageSeo {
  /** <title> 및 검색결과 제목. 키워드를 앞에, 브랜드를 뒤에. */
  title: string
  /** meta description. 한글 80~110자 권장. */
  description: string
  /** 공유 카드용 제목. 생략 시 title 사용. 검색용보다 사람 말투로 써도 된다. */
  ogTitle?: string
  /** 공유 카드용 설명. 생략 시 description 사용. */
  ogDescription?: string
}

/** 홈 기본값 — index.html의 정적 메타와 동일하게 유지할 것 */
export const DEFAULT_SEO: PageSeo = {
  title: '디지털사이니지 · 키오스크 제작 솔루션 | 바로스퀘어 BaroSquare',
  description:
    '드래그 한 번으로 전자메뉴판·키오스크·미디어월을 만들고 전세계 어디서나 원격 송출하세요. 업종별 템플릿과 시간대별 자동 전환을 지원하는 클라우드 디지털사이니지 솔루션, 30일 무료 체험.',
  ogTitle: '디지털 사이니지를 바로 만들어보세요 | 바로스퀘어',
  ogDescription:
    '키오스크, 전자메뉴판, 미디어월까지. 코딩 없이 드래그로 만들고 전세계 어디서나 원격으로 관리하세요.',
}

/**
 * 색인 대상에서 제외할 경로 접두사.
 * 로그인 뒤에서만 의미가 있거나 내용이 얇은 화면들 — 색인되면 사이트 전체 품질 평가를 깎아먹는다.
 * 랜딩(/Home/*)은 여기 절대 넣지 말 것.
 */
const NOINDEX_PREFIXES = [
  '/Admin',
  '/My',
  '/Mobile',
  '/EditContent',
  '/EditViewer',
  '/Player',
  '/ContentPlay',
  '/SubLogin',
  '/ServiceAgree',
  '/Home/BaroLogin',
  '/404',
  // 테스트 라우트
  '/HelloWorld',
  '/Fablic',
  '/GroupTest',
  '/SaveLoad',
  '/Test',
  '/DragDrop',
  '/EditWindow',
  '/EditContentFab',
]

/** 경로 → 메타. 키는 쿼리/해시를 뗀 pathname 이다. */
export const SEO_MAP: Record<string, PageSeo> = {
  '/Home/InfoMain': DEFAULT_SEO,

  '/Home/InfoService': {
    title: '누구나 쉬운 디지털사이니지 제작 서비스 | 바로스퀘어',
    description:
      '템플릿을 고르고 문구만 바꾸면 끝. 디자인이나 코딩 경험이 없어도 전문가 수준의 사이니지·키오스크 콘텐츠를 만들 수 있는 바로스퀘어 제작 서비스를 소개합니다.',
  },
  '/Home/InfoProject': {
    title: '업종별 디지털사이니지 활용사례 | 바로스퀘어',
    description:
      '카페·프랜차이즈·병원·부동산·호텔·오피스·박물관·행사장까지. 업종별로 디지털사이니지와 키오스크를 어떻게 활용하는지 실제 화면과 함께 확인하세요.',
  },
  '/Home/InfoProduct': {
    title: '디지털사이니지 제품 구매 안내 | 바로스퀘어',
    description:
      '바로스퀘어가 직접 공급하는 디지털사이니지 제품을 소개합니다. 설치하면 바로 켜지는 완제품 구성을 확인하고 네이버 스마트스토어에서 바로 구매하세요.',
  },
  '/Home/InfoProgram': {
    title: '플랫폼별 사이니지 플레이어 (안드로이드·구글TV·webOS·윈도우) | 바로스퀘어',
    description:
      '안드로이드 셋톱, 구글TV, LG webOS, 윈도우 PC까지. 쓰던 디스플레이 그대로 설치해 바로 송출할 수 있는 바로스퀘어 전용 플레이어를 안내합니다.',
  },
  '/Home/InfoPrice': {
    title: '디지털사이니지 요금제 · 가격 안내 | 바로스퀘어',
    description:
      '디바이스 수와 사용 방식에 맞춘 합리적인 요금제. 무료 요금제로 먼저 써보고 필요할 때 전환하세요. 신용카드 정기결제와 언제든 해지를 지원합니다.',
  },
  '/Home/InfoBlog': {
    title: '디지털사이니지 트렌드 뉴스레터 | 바로스퀘어',
    description:
      '최신 디지털사이니지 트렌드와 매장 운영 팁, 실제 활용 사례를 정리해 전해드립니다. 비즈니스에 바로 적용할 수 있는 정보를 만나보세요.',
  },
  '/Home/Contact': {
    title: '도입 문의 · 견적 상담 | 바로스퀘어',
    description:
      '매장 규모와 설치 환경에 맞는 디지털사이니지 구성을 상담해 드립니다. 견적·설치·운영 문의를 남겨주시면 담당자가 연락드립니다.',
  },
  '/Home/BaroJoin': {
    title: '회원가입 · 30일 무료 체험 | 바로스퀘어',
    description:
      '가입 즉시 에디터와 업종별 템플릿을 사용할 수 있습니다. 30일 무료 체험으로 바로스퀘어의 사이니지 제작과 원격 관리 기능을 직접 확인해보세요.',
  },
  '/Home/BaroLogin': {
    title: '로그인 | 바로스퀘어 BaroSquare',
    description: '바로스퀘어 계정으로 로그인하고 콘텐츠와 디바이스를 관리하세요.',
  },

  // 업종별 활용사례
  '/Home/InfoRef1': {
    title: '부동산 · 공인중개사 매물 디스플레이 활용사례 | 바로스퀘어',
    description:
      '창가를 지나던 발걸음을 멈추게 하는 매물 디스플레이. 신규 매물과 시세를 사무실에서 바로 업데이트하고 시간대별로 다른 매물을 노출하는 부동산 사이니지 활용법.',
  },
  '/Home/InfoRef2': {
    title: '카페 · 레스토랑 전자메뉴판, 주문 키오스크 활용사례 | 바로스퀘어',
    description:
      '아침엔 모닝 메뉴, 점심엔 런치 세트로 자동 전환되는 전자메뉴판. 셀프 주문 키오스크와 웨이팅 등록까지 한 번에 운영하는 카페·외식 매장 활용 사례.',
  },
  '/Home/InfoRef3': {
    title: '병원 · 의료기관 대기실 안내 사이니지 활용사례 | 바로스퀘어',
    description:
      '대기 순번과 진료 호출을 자동으로 표시하고 남는 시간엔 건강 정보를 내보냅니다. 환자 만족도와 접수 응대 부담을 함께 개선하는 의료기관 사이니지 활용법.',
  },
  '/Home/InfoRef4': {
    title: '프랜차이즈 전 매장 통합 사이니지 관리 사례 | 바로스퀘어',
    description:
      '본사에서 전국 매장의 메뉴·프로모션·공지를 한 번에 동시 업데이트. 매장별 예외 설정까지 지원해 브랜드 일관성을 지키는 프랜차이즈 통합 관리 사례.',
  },
  '/Home/InfoRef5': {
    title: '호텔 · 숙박업 로비 사이니지, 셀프 체크인 활용사례 | 바로스퀘어',
    description:
      '로비 웰컴 화면부터 셀프 체크인 키오스크, 부대시설·조식 안내와 주변 관광정보까지. 첫인상부터 서비스 품질을 끌어올리는 호텔 사이니지 활용 사례.',
  },
  '/Home/InfoRef6': {
    title: '기업 · 오피스 사내 공지 사이니지 활용사례 | 바로스퀘어',
    description:
      '회의실 앞 예약 현황 태블릿, 로비 안내판, 사내 공지 화면을 하나의 대시보드에서 관리. 스마트 오피스를 구현하는 기업용 사이니지 활용 사례.',
  },
  '/Home/InfoRef7': {
    title: '박물관 · 미술관 전시 안내 사이니지 활용사례 | 바로스퀘어',
    description:
      '작품 설명, 다국어 안내, 관람 동선과 인터랙티브 키오스크까지. 구경을 경험으로 바꾸는 박물관·미술관 디지털 안내 시스템 활용 사례.',
  },
  '/Home/InfoRef8': {
    title: '행사 · 전시회 현장 안내 사이니지 활용사례 | 바로스퀘어',
    description:
      '참가자 셀프 체크인, 부스 위치와 발표 일정 실시간 안내, 현장 투표까지. 짧은 행사 시간을 최대한 활용하는 전시·이벤트 사이니지 운영 사례.',
  },
  '/Home/InfoRef9': {
    title: '매장 앞 실시간 리뷰 재생기 활용사례 | 바로스퀘어',
    description:
      '매장 앞은 언제나 상위노출 1순위 공간입니다. 실제 고객 리뷰를 자동으로 수집·재생해 지나가는 손님의 시선과 신뢰를 동시에 잡는 리뷰 재생기 활용법.',
  },

  // 정책
  '/Home/TermsService': {
    title: '이용약관 | 바로스퀘어 BaroSquare',
    description: '바로스퀘어 서비스 이용약관입니다.',
  },
  '/Home/PrivacyPolicy': {
    title: '개인정보처리방침 | 바로스퀘어 BaroSquare',
    description: '바로스퀘어의 개인정보 수집·이용·보관 및 파기 기준을 안내합니다.',
  },
  '/Home/RefundPolicy': {
    title: '환불정책 | 바로스퀘어 BaroSquare',
    description: '바로스퀘어 구독 요금제의 취소 및 환불 기준을 안내합니다.',
  },
}

/** name/property 메타 태그를 찾아 갱신한다. 없으면 만든다. */
function setMeta(kind: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(kind, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', url)
}

function isNoIndex(path: string): boolean {
  return NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p))
}

/**
 * 현재 경로에 맞는 메타를 head에 반영한다.
 * @param path 쿼리/해시를 제외한 pathname (예: '/Home/InfoPrice')
 */
export function applySeo(path: string) {
  // 매핑에 없는 경로(앱 내부 화면 등)는 기본값을 쓰되 canonical은 해당 경로로 둔다.
  const seo = SEO_MAP[path] ?? DEFAULT_SEO
  const canonical = `${SITE_URL}${path === '/' ? '/Home/InfoMain' : path}`
  const ogTitle = seo.ogTitle ?? seo.title
  const ogDescription = seo.ogDescription ?? seo.description

  document.title = seo.title
  setMeta('name', 'description', seo.description)
  setCanonical(canonical)

  setMeta('property', 'og:title', ogTitle)
  setMeta('property', 'og:description', ogDescription)
  setMeta('property', 'og:url', canonical)
  setMeta('property', 'og:image', OG_IMAGE)

  setMeta('name', 'twitter:title', ogTitle)
  setMeta('name', 'twitter:description', ogDescription)
  setMeta('name', 'twitter:image', OG_IMAGE)

  setMeta(
    'name',
    'robots',
    isNoIndex(path) ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'
  )
}
